import React, { useState, useRef, useEffect } from 'react';
import { Box, Textarea, Text, Alert, AlertIcon } from '@chakra-ui/react';

/**
 * JSON Formatter Component with Optional Highlighting Control
 */
const JSONFormatter = ({ 
    value, 
    onChange, 
    placeholder, 
    rows = 10, 
    isDisabled = false,
    bg = 'gray.50',
    showValidation = true,
    showFormatButton = true,
    showHighlightButton = true,
    autoHighlight = false,
    autoSize = false,
    minRows = 3,
    maxRows = 30,
    ...props 
}) => {
    // State management for validation, formatting, and highlighting
    const [currentValue, setCurrentValue] = useState(value || '');
    const [validationError, setValidationError] = useState(null);
    const [isFormatting, setIsFormatting] = useState(false);
    const [formatSuccess, setFormatSuccess] = useState(false);
    const [highlightedContent, setHighlightedContent] = useState('');
    const [isHighlighted, setIsHighlighted] = useState(false);
    const textareaRef = useRef(null);
    const highlightRef = useRef(null);
    const containerRef = useRef(null);
    const lastValidJSON = useRef('');

    /**
     * Calculate optimal rows for content auto-sizing
     * Determines appropriate height based on content length and structure
     */
    const calculateOptimalRows = () => {
        if (!autoSize || !currentValue) return rows;
        
        // Count actual line breaks in content
        const lineCount = currentValue.split('\n').length;
        
        // Add buffer for better readability
        const calculatedRows = Math.max(lineCount + 1, minRows);
        
        // Ensure within reasonable limits
        return Math.min(calculatedRows, maxRows);
    };

    /**
     * Generate highlighted HTML content from JSON text
     * Uses a token-based approach to prevent nested span issues
     * Applies comprehensive syntax highlighting while preserving original formatting
     */
    const generateHighlightedHTML = (text) => {
        if (!text) return '';

        try {
            // First, validate that it's proper JSON
            JSON.parse(text);

            // Escape HTML characters for safe rendering
            let processedText = text
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;');

            // Use unique placeholders to prevent conflicts
            const placeholders = {};
            let placeholderCounter = 0;

            // Helper function to create unique placeholder
            const createPlaceholder = (content, className) => {
                const placeholder = `__PLACEHOLDER_${placeholderCounter++}__`;
                placeholders[placeholder] = `<span class="${className}">${content}</span>`;
                return placeholder;
            };

            // Step 1: Process JSON keys (quoted strings followed by colon)
            processedText = processedText.replace(
                /(&quot;[^&]*?&quot;)(\s*:\s*)/g, 
                (match, key, colon) => {
                    const keyPlaceholder = createPlaceholder(key, 'json-key');
                    const colonPlaceholder = createPlaceholder(colon, 'json-colon');
                    return keyPlaceholder + colonPlaceholder;
                }
            );

            // Step 2: Process string values (remaining quoted strings)
            processedText = processedText.replace(
                /(&quot;[^&]*?&quot;)/g,
                (match, str) => {
                    // Check if this string is not already processed (not a placeholder)
                    if (!str.includes('__PLACEHOLDER_')) {
                        return createPlaceholder(str, 'json-string-value');
                    }
                    return str;
                }
            );

            // Step 3: Process numbers
            processedText = processedText.replace(
                /\b(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)\b/g,
                (match, num) => createPlaceholder(num, 'json-number')
            );

            // Step 4: Process boolean values
            processedText = processedText.replace(
                /\b(true|false)\b/g,
                (match, bool) => createPlaceholder(bool, 'json-boolean')
            );

            // Step 5: Process null values
            processedText = processedText.replace(
                /\b(null)\b/g,
                (match, nullVal) => createPlaceholder(nullVal, 'json-null')
            );

            // Step 6: Process structural elements
            processedText = processedText.replace(
                /([{}\[\]])/g,
                (match, bracket) => createPlaceholder(bracket, 'json-bracket')
            );

            // Step 7: Process commas
            processedText = processedText.replace(
                /(,)/g,
                (match, comma) => createPlaceholder(comma, 'json-comma')
            );

            // Step 8: Replace all placeholders with actual HTML
            Object.keys(placeholders).forEach(placeholder => {
                processedText = processedText.replace(
                    new RegExp(placeholder, 'g'),
                    placeholders[placeholder]
                );
            });

            // Step 9: Convert newlines and preserve spaces
            processedText = processedText
                .replace(/\r?\n/g, '<br>')
                .replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;')
                .replace(/  /g, '&nbsp;&nbsp;');

            return processedText;
        } catch (error) {
            console.warn('⚠️ JSON parsing failed for highlighting:', error);
            // If JSON is invalid, return plain text with basic escaping
            return text
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/\r?\n/g, '<br>')
                .replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;')
                .replace(/  /g, '&nbsp;&nbsp;');
        }
    };

    /**
     * Validate JSON syntax and structure with enhanced error reporting
     * Provides detailed error messages for common JSON issues
     * Returns validation result with error details and suggestions
     */
    const validateJSON = (jsonString) => {
        if (!jsonString.trim()) {
            return { isValid: true, error: null };
        }

        try {
            const parsed = JSON.parse(jsonString);
            
            // Additional validation for report parameters structure
            if (Array.isArray(parsed)) {
                for (let i = 0; i < parsed.length; i++) {
                    const param = parsed[i];
                    if (typeof param !== 'object' || param === null) {
                        return {
                            isValid: false,
                            error: `Parameter ${i + 1} must be an object`
                        };
                    }
                    
                    // Validate required fields for report parameters
                    const requiredFields = ['category', 'type', 'index'];
                    for (const field of requiredFields) {
                        if (!(field in param)) {
                            return {
                                isValid: false,
                                error: `Parameter ${i + 1} missing required field: ${field}`
                            };
                        }
                    }
                    
                    // Validate field types with enhanced checking
                    if (typeof param.index !== 'number' || param.index < 0) {
                        return {
                            isValid: false,
                            error: `Parameter ${i + 1} index must be a non-negative number`
                        };
                    }
                    
                    if (typeof param.category !== 'string' || !param.category.trim()) {
                        return {
                            isValid: false,
                            error: `Parameter ${i + 1} category must be a non-empty string`
                        };
                    }
                    
                    if (typeof param.type !== 'string' || !param.type.trim()) {
                        return {
                            isValid: false,
                            error: `Parameter ${i + 1} type must be a non-empty string`
                        };
                    }

                    // Validate optional fields if present
                    if (param.name && (typeof param.name !== 'string' || param.name.length > 100)) {
                        return {
                            isValid: false,
                            error: `Parameter ${i + 1} name must be a string under 100 characters`
                        };
                    }

                    if (param.required !== undefined && typeof param.required !== 'boolean') {
                        return {
                            isValid: false,
                            error: `Parameter ${i + 1} required field must be boolean`
                        };
                    }
                }
            }
            
            return { isValid: true, error: null };
        } catch (error) {
            // Parse error messages to provide user-friendly feedback
            let errorMessage = error.message;
            
            // Common JSON error patterns with helpful suggestions
            if (errorMessage.includes('Unexpected token')) {
                const match = errorMessage.match(/Unexpected token (.+) in JSON at position (\d+)/);
                if (match) {
                    const [, token, position] = match;
                    errorMessage = `Unexpected character '${token}' at position ${position}. Check for missing quotes or commas.`;
                }
            } else if (errorMessage.includes('Unexpected end of JSON input')) {
                errorMessage = 'Incomplete JSON - missing closing bracket, brace, or quote';
            } else if (errorMessage.includes('Expected property name')) {
                errorMessage = 'Expected property name in quotes. Object keys must be strings.';
            } else if (errorMessage.includes('Unexpected string')) {
                errorMessage = 'Unexpected string. Check for missing commas between properties.';
            }
            
            return {
                isValid: false,
                error: errorMessage
            };
        }
    };

    /**
     * Format JSON string with proper indentation and structure
     * Only formats when manually triggered via button click or auto-highlight enabled
     * Provides comprehensive error handling and user feedback
     */
    const formatJSON = (jsonString) => {
        if (!jsonString.trim()) {
            return jsonString;
        }

        try {
            // Attempt to parse and format with enhanced options
            const parsed = JSON.parse(jsonString);
            const formatted = JSON.stringify(parsed, null, 2);
            
            // Store as last valid JSON for fallback
            lastValidJSON.current = formatted;
            
            return formatted;
        } catch (error) {
            throw new Error(`Invalid JSON: ${error.message}`);
        }
    };

    /**
     * Apply automatic highlighting for view mode
     * Used when autoHighlight prop is true
     */
    const applyAutoHighlighting = (text) => {
        if (!text.trim()) {
            return;
        }
        
        try {
            const highlighted = generateHighlightedHTML(text);
            
            if (highlighted) {
                setHighlightedContent(highlighted);
                setIsHighlighted(true);
            } 
        } catch (error) {
            console.error('❌ Auto-highlighting failed:', error);
        }
    };

    /**
     * Handle text change with validation only in edit mode
     * No auto-formatting, only validation for immediate feedback
     * Maintains simple text input behavior
     * Preserves auto-highlighting in view mode
     */
    const handleTextChange = (e) => {
        const newValue = e.target.value;
        
        setCurrentValue(newValue);
        setFormatSuccess(false); // Reset format success indicator
        
        // If currently highlighted, turn off highlighting to show normal text (unless auto-highlight mode is enabled)
        if (isHighlighted && !autoHighlight) {
            setIsHighlighted(false);
            setHighlightedContent('');
        }
        
        // Immediate validation for user feedback
        if (showValidation) {
            const validation = validateJSON(newValue);
            setValidationError(validation.error);
        }
        
        // Trigger change callback with current value
        onChange(newValue);
    };

    /**
     * Handle manual format button click with conditional highlighting
     * Formats JSON immediately when requested by user
     * Never applies highlighting when showHighlightButton is false
     * Provides user feedback on formatting success or failure
     */
    const handleManualFormat = () => {
        if (!currentValue.trim()) {
            if (showValidation) {
                setValidationError('No content to format');
            }
            return;
        }
        
        setIsFormatting(true);
        
        try {
            const formatted = formatJSON(currentValue);
            
            if (formatted !== currentValue) {
                setCurrentValue(formatted);
                onChange(formatted);
                
                // Clear any existing validation errors if formatting succeeds
                if (showValidation) {
                    setValidationError(null);
                }
                
                // Show success indicator
                setFormatSuccess(true);
                setTimeout(() => setFormatSuccess(false), 2000); // Clear after 2 seconds
                
                // Focus back to textarea without highlighting when showHighlightButton is false
                setTimeout(() => {
                    if (textareaRef.current) {
                        textareaRef.current.focus();
                    }
                }, 50);
            } else {
                // JSON was already formatted - show success
                setFormatSuccess(true);
                setTimeout(() => setFormatSuccess(false), 1000);
            }
        } catch (error) {
            if (showValidation) {
                setValidationError(error.message);
            }
            console.error('Manual formatting failed:', error);
        } finally {
            setIsFormatting(false);
        }
    };

    /**
     * Toggle between highlighted and normal view
     * Allows users to switch between syntax highlighting and plain text
     */
    const toggleHighlighting = () => {
        if (isHighlighted) {
            setIsHighlighted(false);
            setHighlightedContent('');
        } else {
            try {
                const highlighted = generateHighlightedHTML(currentValue);
                setHighlightedContent(highlighted);
                setIsHighlighted(true);
                // Sync scroll position after highlighting
                setTimeout(() => syncScrollPosition(), 50);
            } catch (error) {
                console.error('Highlighting toggle failed:', error);
            }
        }
    };

    /**
     * Synchronize scroll position between textarea and highlight overlay
     * Fixed implementation to ensure proper visibility
     */
    const syncScrollPosition = () => {
        if (isHighlighted && textareaRef.current && highlightRef.current) {
            const textarea = textareaRef.current;
            const highlight = highlightRef.current;
            
            // Apply scroll position with proper transform
            highlight.scrollTop = textarea.scrollTop;
            highlight.scrollLeft = textarea.scrollLeft;
            
            // Also use transform as backup
            highlight.style.transform = `translate(-${textarea.scrollLeft}px, -${textarea.scrollTop}px)`;
        }
    };

    /**
     * Handle scroll events with improved synchronization
     */
    const handleScroll = () => {
        syncScrollPosition();
    };

    /**
     * Handle paste events with validation and optional auto-formatting
     * Validates pasted JSON content and applies highlighting if enabled
     * Provides immediate feedback on pasted content validity
     * Auto-highlighting works regardless of showHighlightButton setting
     */
    const handlePaste = (e) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');
        const textarea = textareaRef.current;
        
        if (textarea) {
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const newValue = currentValue.substring(0, start) + pastedText + currentValue.substring(end);
            
            setCurrentValue(newValue);
            onChange(newValue);
            
            // Validate pasted content
            if (showValidation) {
                const validation = validateJSON(newValue);
                setValidationError(validation.error);
            }

            // Auto-highlight if enabled and content is valid JSON
            if (autoHighlight) {
                try {
                    JSON.parse(newValue);
                    applyAutoHighlighting(newValue);
                } catch (error) {
                    // Invalid JSON, don't highlight
                }
            }
            
            // Set cursor position after paste
            setTimeout(() => {
                if (textareaRef.current) {
                    const newCursorPos = start + pastedText.length;
                    textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
                    textareaRef.current.focus();
                    if (autoHighlight || showHighlightButton) {
                        syncScrollPosition();
                    }
                }
            }, 10);
        }
    };

    // Initialize value when value prop changes
    useEffect(() => {
        if (value !== currentValue && !isFormatting) {
            setCurrentValue(value || '');
            if (showValidation && value) {
                const validation = validateJSON(value);
                setValidationError(validation.error);
            }
            
            // Auto-highlight if enabled regardless of showHighlightButton setting
            if (autoHighlight && value?.trim()) {
                applyAutoHighlighting(value);
            }
        }
    }, [value, autoHighlight]);

    // Apply auto-highlighting on mount if enabled - with delay for proper rendering
    useEffect(() => {
        if (autoHighlight && currentValue?.trim()) {
            // Add slight delay to ensure DOM is ready
            setTimeout(() => {
                applyAutoHighlighting(currentValue);
            }, 100);
        }
    }, [autoHighlight]);

    // Force highlighting when becoming visible (for view modal)
    useEffect(() => {
        if (autoHighlight && currentValue?.trim() && !isHighlighted) {
            setTimeout(() => {
                applyAutoHighlighting(currentValue);
            }, 200);
        }
    }, [autoHighlight, currentValue]);

    // Sync scroll position when highlighting state changes
    useEffect(() => {
        if (isHighlighted) {
            setTimeout(() => syncScrollPosition(), 100);
        }
    }, [isHighlighted]);

    return (
        <Box width="100%" position="relative">
            {/* Enhanced CSS styles for JSON syntax highlighting with fixed scroll sync */}
            <style>
                {`
                    .json-editor-container {
                        position: relative;
                        font-family: 'Consolas', 'Monaco', 'Courier New', 'SF Mono', monospace;
                        font-size: 14px;
                        line-height: 1.5;
                        border-radius: 6px;
                        overflow: hidden;
                    }
                    
                    .json-highlight-overlay {
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        padding: 8px 12px;
                        margin: 0;
                        border: 2px solid transparent;
                        background: transparent;
                        color: inherit;
                        pointer-events: none;
                        overflow: hidden;
                        white-space: pre-wrap;
                        word-wrap: break-word;
                        word-break: break-word;
                        z-index: 1;
                        box-sizing: border-box;
                        font-family: inherit;
                        font-size: inherit;
                        line-height: inherit;
                        border-radius: 6px;
                    }
                    
                    .json-textarea {
                        position: relative;
                        z-index: 2;
                        font-family: inherit;
                        font-size: inherit;
                        line-height: inherit;
                        resize: vertical;
                        border: 2px solid #E2E8F0;
                        border-radius: 6px;
                        width: 100%;
                    }
                    
                    .json-textarea:focus {
                        border-color: #3182CE;
                        box-shadow: 0 0 0 1px #3182CE;
                    }
                    
                    /* Highlighted mode styles */
                    .json-textarea.highlighted {
                        background: transparent !important;
                        color: transparent !important;
                        caret-color: #2D3748;
                    }
                    
                    .json-textarea.highlighted:focus {
                        background: transparent !important;
                        color: transparent !important;
                        caret-color: #2D3748;
                    }
                    
                    .json-textarea.highlighted::selection {
                        background: rgba(49, 130, 206, 0.3);
                    }
                    
                    /* JSON Syntax Highlighting Colors - Light Theme */
                    .json-key {
                        color: #0066CC;
                        font-weight: bold;
                    }
                    
                    .json-string-value {
                        color: #DD1144;
                    }
                    
                    .json-number {
                        color: #009999;
                        font-weight: 500;
                    }
                    
                    .json-boolean {
                        color: #990073;
                        font-weight: bold;
                    }
                    
                    .json-null {
                        color: #999988;
                        font-style: italic;
                        font-weight: bold;
                    }
                    
                    .json-bracket {
                        color: #445588;
                        font-weight: bold;
                    }
                    
                    .json-colon {
                        color: #445588;
                        font-weight: bold;
                    }
                    
                    .json-comma {
                        color: #445588;
                    }
                    
                    /* Dark theme styles */
                    .chakra-ui-dark .json-textarea {
                        border-color: #4A5568;
                    }
                    
                    .chakra-ui-dark .json-textarea:focus {
                        border-color: #63B3ED;
                        box-shadow: 0 0 0 1px #63B3ED;
                    }
                    
                    .chakra-ui-dark .json-textarea.highlighted {
                        caret-color: #E2E8F0;
                    }
                    
                    /* JSON Syntax Highlighting Colors - Dark Theme */
                    .chakra-ui-dark .json-key {
                        color: #66B3FF;
                        font-weight: bold;
                    }
                    
                    .chakra-ui-dark .json-string-value {
                        color: #FFB3BA;
                    }
                    
                    .chakra-ui-dark .json-number {
                        color: #66FFCC;
                        font-weight: 500;
                    }
                    
                    .chakra-ui-dark .json-boolean {
                        color: #FF66D9;
                        font-weight: bold;
                    }
                    
                    .chakra-ui-dark .json-null {
                        color: #CCCCCC;
                        font-style: italic;
                        font-weight: bold;
                    }
                    
                    .chakra-ui-dark .json-bracket {
                        color: #C9A9DD;
                        font-weight: bold;
                    }
                    
                    .chakra-ui-dark .json-colon {
                        color: #C9A9DD;
                        font-weight: bold;
                    }
                    
                    .chakra-ui-dark .json-comma {
                        color: #C9A9DD;
                    }
                    
                    /* Button styles */
                    .format-button {
                        font-size: 12px;
                        color: #3182CE;
                        background: none;
                        border: none;
                        cursor: pointer;
                        text-decoration: underline;
                        padding: 4px 8px;
                        border-radius: 4px;
                        transition: background-color 0.2s;
                    }
                    
                    .format-button:hover:not(:disabled) {
                        background-color: #E6F3FF;
                    }
                    
                    .format-button:disabled {
                        color: #A0AEC0;
                        cursor: not-allowed;
                        text-decoration: none;
                    }
                    
                    .format-success {
                        color: #38A169;
                        font-weight: medium;
                    }
                    
                    .highlighted-indicator {
                        color: #0066CC;
                        font-weight: medium;
                    }
                `}
            </style>
            
            {/* Validation Error Display */}
            {showValidation && validationError && (
                <Alert status="error" borderRadius="md" mb={3} size="sm">
                    <AlertIcon />
                    <Box>
                        <Text fontSize="sm" fontWeight="medium">JSON Validation Error:</Text>
                        <Text fontSize="xs" mt={1}>{validationError}</Text>
                    </Box>
                </Alert>
            )}
            
            <Box ref={containerRef} className="json-editor-container">
                {/* Highlighted background overlay - visible when highlighted (auto or manual) */}
                {isHighlighted && (
                    <Box
                        ref={highlightRef}
                        className="json-highlight-overlay"
                        dangerouslySetInnerHTML={{ __html: highlightedContent }}
                    />
                )}
                
                {/* JSON Textarea with Auto-Sizing Support */}
                <Textarea
                    ref={textareaRef}
                    className={`json-textarea ${isHighlighted ? 'highlighted' : ''}`}
                    value={currentValue}
                    onChange={handleTextChange}
                    onPaste={handlePaste}
                    onScroll={isHighlighted ? handleScroll : undefined}
                    placeholder={placeholder || 'Enter JSON here...'}
                    rows={autoSize ? calculateOptimalRows() : rows}
                    isDisabled={isDisabled}
                    bg={isHighlighted ? 'transparent' : bg}
                    resize={autoSize ? "none" : "vertical"}
                    spellCheck={false}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    {...props}
                />
            </Box>
            
            {/* Footer with Format Button and Status */}
            <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                <Text fontSize="xs" color="gray.500">
                    {currentValue.length} characters
                    {autoSize && ` • Auto-sized: ${calculateOptimalRows()} rows`}
                </Text>
                
                <Box display="flex" alignItems="center" gap={3}>
                    {/* Validation Success Indicator */}
                    {showValidation && !validationError && currentValue.trim() && (
                        <Text fontSize="xs" color="green.500" fontWeight="medium">
                            ✓ Valid JSON
                        </Text>
                    )}
                    
                    {/* Highlight Status Indicators - only show when highlighting is enabled */}
                    {showHighlightButton && isHighlighted && (
                        <Text fontSize="xs" className="highlighted-indicator">
                            {autoHighlight ? '✓ Auto-Highlighted' : '✓ Highlighted'}
                        </Text>
                    )}
                    
                    {/* Format Success Indicator */}
                    {formatSuccess && (
                        <Text fontSize="xs" className="format-success">
                            ✓ Formatted
                        </Text>
                    )}
                    
                    {/* Format JSON Button - only show if not auto-highlight mode */}
                    {showFormatButton && !autoHighlight && (
                        <button
                            className="format-button"
                            onClick={handleManualFormat}
                            disabled={isDisabled || isFormatting || !currentValue.trim()}
                        >
                            {isFormatting ? 'Formatting...' : 'Format JSON'}
                        </button>
                    )}

                    {/* Toggle Highlight Button - show when not in auto-highlight mode */}
                    {!autoHighlight && currentValue.trim() && !isFormatting && (
                        <button
                            className="format-button"
                            onClick={toggleHighlighting}
                            disabled={isDisabled}
                        >
                            {isHighlighted ? 'Remove Highlighting' : 'Highlight JSON'}
                        </button>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default JSONFormatter;